/* ==========================================================================
   SKEW PREMIUM - JavaScript
   ========================================================================== */

(function () {
  'use strict';

  // エントリポイント：DOMContentLoaded で各機能を順に初期化
  // - 各 init 関数は独立しており、片方が失敗しても他に影響しない構造
  // - 関数宣言はホイストされるため、ここで先に呼び出して以下で定義してよい
  document.addEventListener('DOMContentLoaded', function () {
    initMainVisual();
    initFadeIn();
    initSliders();
  });

  // ============================================================
  // Main Visual - 初回ロード演出（背景マスク → コピー+商品ズーム）
  // ・SCSS の .is-playing 配下で animation 発火
  // ・JS では 各アニメの開始時刻を CSS変数として注入し、最後に .is-playing を付与する
  // ・1ページロードにつき1回のみ実行
  // ・Web フォント読み込み完了を待ってから発火（フォント切り替わり時のチラつき防止）
  // ============================================================
  function initMainVisual() {
    var mv = document.querySelector('.main-visual');
    if (!mv) return;

    // タイミング設計（総尺 約2.1s、コピーだけ着地が 200ms 延びて 2108ms 終了）：
    //   ・背景マスク：ロードから 300ms 後に開始、1308ms かけて完了 → 1608ms 時点で完了
    //   ・以降は **ステージごとに個別 OVERLAP** でカスケード接続：
    //     - OVERLAP_TO_BOOST = 600（マスク→BOOST）  → BOOST 開始 1008ms
    //     - OVERLAP_TO_REST  = 900（BOOST→REST）   → REST  開始 1108ms（BOOST→REST 間 100ms）
    //     - OVERLAP_TO_COPY  = 900（REST→コピー）  → コピー開始 1208ms（REST→コピー 間 100ms）
    //     - OVERLAP_TO_CTA   = 800（コピー→CTA）   → CTA   開始 1308ms（コピー→CTA 間 100ms = 900-800）
    //   ・全ステージ間隔が均一 100ms の「行進カスケード」で連続感を最大化
    //   ・コピーだけ duration 900ms と長め（CTA より後まで残ってじわっと着地する余韻）
    var MASK_DELAY = 300;          // ロードからマスク開始までの待機時間（ms）
    var MASK_DURATION = 1308;      // マスク展開の所要時間（ms）— SCSS デフォルトと一致
    var ZOOM_DURATION = 1000;      // 商品ズーム1個あたりの所要時間（ms）— SCSS と一致
    var COPY_ZOOM_DURATION = 900;  // コピーズームの所要時間（ms）— SCSS と一致。長めの余韻
    var OVERLAP_TO_BOOST = 600;    // マスク完了 N ms 前から BOOST 発動
    var OVERLAP_TO_REST  = 900;    // BOOST 完了 N ms 前から REST  発動（間隔 100ms）
    var OVERLAP_TO_COPY  = 900;    // REST  完了 N ms 前からコピー発動（間隔 100ms）
    var OVERLAP_TO_CTA   = 800;    // コピー完了 N ms 前から CTA 発動（間隔 100ms、コピー duration 延長に合わせて 600→800）

    // カスケード算出：各ステージは個別の OVERLAP を使用
    var maskEnd = MASK_DELAY + MASK_DURATION;          // マスク完了 = 1608ms
    var boostDelay = maskEnd - OVERLAP_TO_BOOST;       // BOOST 開始 = 1008ms
    var boostEnd = boostDelay + ZOOM_DURATION;         // BOOST 完了 = 2008ms
    var restDelay = boostEnd - OVERLAP_TO_REST;        // REST  開始 = 1108ms
    var restEnd = restDelay + ZOOM_DURATION;           // REST  完了 = 2108ms
    var copyDelay = restEnd - OVERLAP_TO_COPY;         // コピー開始 = 1208ms
    var copyEnd = copyDelay + COPY_ZOOM_DURATION;      // コピー完了 = 1908ms
    var ctaDelay = copyEnd - OVERLAP_TO_CTA;           // CTA   開始 = 1308ms

    // 背景マスク：300ms 待ってから 1308ms かけて展開
    mv.style.setProperty('--mv-mask-delay', MASK_DELAY + 'ms');
    mv.style.setProperty('--mv-mask-duration', MASK_DURATION + 'ms');

    // 商品ズーム：各 OVERLAP で算出した時刻に発動
    mv.style.setProperty('--mv-zoom-delay-boost', boostDelay + 'ms');
    mv.style.setProperty('--mv-zoom-delay-rest', restDelay + 'ms');

    // コピーズーム：REST 完了の OVERLAP_TO_COPY 前から開始
    mv.style.setProperty('--mv-copy-delay', copyDelay + 'ms');

    // CTAフェードイン：コピー完了の 300ms 前から発火
    //   ・CSS変数は cta-wrap 要素に直接付与（sibling のため main-visual からは継承不可）
    var ctaWrap = document.querySelector('.main-visual__cta-wrap');
    if (ctaWrap) {
      ctaWrap.style.setProperty('--mv-cta-delay', ctaDelay + 'ms');
    }

    // 発火関数
    // ・CSS変数は style.setProperty で同期適用されるため rAF は不要
    // ・バックグラウンドタブで rAF が停止する環境でも確実に発火させるため同期で classList を更新
    var fired = false;
    function startMV() {
      if (fired) return;
      fired = true;
      mv.classList.add('is-playing');
    }

    // Web フォント読み込み完了の検出
    // - Adobe Typekit が <html> に付与する wf-active（成功） / wf-inactive（失敗）を待つ
    //   → これが「フォントの @font-face ロード＆描画適用が完了した最も確実なシグナル」
    // - document.fonts.ready は「現時点でロード中のフォントが無ければ即座に resolve」
    //   する仕様のため、Typekit の @font-face 注入前に DOMContentLoaded が走ると
    //   フォント未ロードのまま resolve してしまう（途中でフォントが入れ替わる原因）
    //   よってここでは使用せず、Typekit のクラス遷移のみを信頼する
    // - セーフティ：3500ms（Typekit の scriptTimeout=3000ms に余裕を足した上限）
    var htmlEl = document.documentElement;

    function isFontResolved() {
      return htmlEl.classList.contains('wf-active') ||
             htmlEl.classList.contains('wf-inactive');
    }

    if (isFontResolved()) {
      startMV();
    } else if ('MutationObserver' in window) {
      var wfObserver = new MutationObserver(function () {
        if (isFontResolved()) {
          wfObserver.disconnect();
          startMV();
        }
      });
      wfObserver.observe(htmlEl, { attributes: true, attributeFilter: ['class'] });
    }

    setTimeout(startMV, 3500);
  }

  // ============================================================
  // フェードイン（Galaxy LP と同仕様）
  // - .premium-js-fade-in を付与した要素をビューポート検出
  // - 同時に入った複数要素は 200ms ずつずらしてスタッガー表示
  // ============================================================
  function initFadeIn() {
    var fadeTargets = document.querySelectorAll('.premium-js-fade-in');

    if ('IntersectionObserver' in window && fadeTargets.length) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry, i) {
            if (entry.isIntersecting) {
              setTimeout(function () {
                entry.target.classList.add('premium-is-visible');
              }, i * 200);
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.15 }
      );

      fadeTargets.forEach(function (el) {
        observer.observe(el);
      });
    } else {
      // フォールバック：IntersectionObserver非対応環境では即時表示
      fadeTargets.forEach(function (el) {
        el.classList.add('premium-is-visible');
      });
    }
  }

  // ============================================================
  // PRODUCTS - SPカルーセル
  // - [data-slider] ごとに独立して動作（CARTRIDGE / COMBO の追加にも対応）
  // - PCではCSS側で .product-slider__controls を非表示・track をgrid化するため
  //   transform は無効化（matchMedia で適用範囲を制限）
  // - 矢印・ドット・端でのループ無し（最初/最後で disabled）
  // - スワイプ（ドラッグ追従型）対応：iOS Safari/Chrome のスクロール優先・
  //   back-swipe 横取り対策。指の動きにカードがリアルタイム追従するため
  //   ユーザー操作として明示的に処理される（Galaxy LP と同パターン）
  // ============================================================
  function initSliders() {
    var sliders = document.querySelectorAll('[data-slider]');
    var spMQ = window.matchMedia('(max-width: 1024px)');

    // iOS Safari 対策: window レベルに passive な touchstart リスナーを置くと
    // 子要素の touch イベントが安定して発火する（これがないと一部状況で
    // track 上の touchstart が iOS Safari に届かない）
    // 中身は意図的に空のままにしておく（リスナーが存在することだけが重要）
    if (sliders.length) {
      window.addEventListener('touchstart', function () {}, { passive: true, capture: true });
    }

    sliders.forEach(function (slider) {
      var track   = slider.querySelector('[data-slider-track]');
      var slides  = slider.querySelectorAll('.product-slider__slide');
      var prevBtn = slider.querySelector('[data-slider-prev]');
      var nextBtn = slider.querySelector('[data-slider-next]');
      var dots    = slider.querySelectorAll('.product-slider__dot');

      if (!track || slides.length === 0) return;

      var index = 0;
      var total = slides.length;

      // 現在の index に対応する translateX を計算（1スライド = slide幅 + gap）
      function getStep() {
        var slideRect = slides[0].getBoundingClientRect();
        var trackStyle = window.getComputedStyle(track);
        var gap = parseFloat(trackStyle.columnGap || trackStyle.gap || 0);
        return slideRect.width + gap;
      }

      function setTransform(translateX, animated) {
        track.style.transition = animated ? 'transform 0.4s ease' : 'none';
        track.style.transform = 'translateX(' + translateX + 'px)';
      }

      function update(animated) {
        if (spMQ.matches) {
          setTransform(-getStep() * index, animated !== false);
        } else {
          // PC：grid配置に戻すため transform / transition をクリア
          track.style.transform = '';
          track.style.transition = '';
        }

        // ドット
        dots.forEach(function (dot, i) {
          dot.classList.toggle('is-active', i === index);
        });

        // 矢印（端で無効化）
        if (prevBtn) prevBtn.disabled = (index === 0);
        if (nextBtn) nextBtn.disabled = (index === total - 1);
      }

      function goTo(i) {
        index = Math.max(0, Math.min(total - 1, i));
        update(true);
      }

      if (prevBtn) prevBtn.addEventListener('click', function () { goTo(index - 1); });
      if (nextBtn) nextBtn.addEventListener('click', function () { goTo(index + 1); });
      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () { goTo(i); });
      });

      // ----- タッチスワイプ（ドラッグ追従型）-----
      var touchStartX = 0;
      var touchStartY = 0;
      var baseTranslate = 0;   // touchstart 時点での translateX 値
      var axisLocked = false;
      var isHorizontal = false;
      var isDragging = false;

      track.addEventListener('touchstart', function (e) {
        if (!spMQ.matches) return;
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
        baseTranslate = -getStep() * index;
        axisLocked = false;
        isHorizontal = false;
        isDragging = true;
        // 追従中はトランジションを切る
        track.style.transition = 'none';
      }, { passive: true });

      track.addEventListener('touchmove', function (e) {
        if (!isDragging || !spMQ.matches) return;
        var dx = e.touches[0].clientX - touchStartX;
        var dy = e.touches[0].clientY - touchStartY;

        // 軸ロック（4pxで早期判定）
        if (!axisLocked) {
          if (Math.abs(dx) > 4 || Math.abs(dy) > 4) {
            axisLocked = true;
            isHorizontal = Math.abs(dx) >= Math.abs(dy);
          } else {
            return;
          }
        }
        if (!isHorizontal) return; // 縦方向はページスクロールに任せる

        // 端での過剰ドラッグに抵抗（ゴムバンド風）
        if ((index === 0 && dx > 0) || (index === total - 1 && dx < 0)) {
          dx = dx * 0.3;
        }

        // 横方向: ページスクロールを抑止して指に追従
        if (e.cancelable) e.preventDefault();
        track.style.transform = 'translateX(' + (baseTranslate + dx) + 'px)';
      }, { passive: false });

      function finishDrag(dx) {
        isDragging = false;
        if (!axisLocked || !isHorizontal) return;
        // 送り判定: スライド幅の15% を超えたらコミット、それ以下はスナップバック
        var step = getStep();
        var threshold = Math.max(30, step * 0.15);
        if (Math.abs(dx) > threshold) {
          goTo(dx > 0 ? index - 1 : index + 1);
        } else {
          update(true); // 元の位置にスナップバック
        }
      }

      track.addEventListener('touchend', function (e) {
        if (!spMQ.matches) return;
        var dx = isDragging ? (e.changedTouches[0].clientX - touchStartX) : 0;
        finishDrag(dx);
      });

      track.addEventListener('touchcancel', function () {
        if (!spMQ.matches) return;
        isDragging = false;
        axisLocked = false;
        isHorizontal = false;
        update(true);
      });

      // ブレイクポイント切り替え時 / リサイズで再計算
      // matchMedia の change が無い古い環境向けに resize にもフック
      if (spMQ.addEventListener) {
        spMQ.addEventListener('change', function () { update(false); });
      }
      window.addEventListener('resize', function () { update(false); });

      update(false);
    });
  }
})();
